import './AppShell.css';
import type { ComponentChildren, ComponentType, JSX } from 'preact';
import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import { reducedMotion } from '../a11y/useReducedMotion';
import { t } from '../copy/strings';
import type { LayoutMode } from '../model/types';
import { enterTv, exitTv } from '../state/actions';
import { useConfig, useHasFocus, useLayout, useRouteState, useUi, useViewKey } from '../state/hooks';
import { isPhoneMode, isWide, LAYOUT, layoutForViewport, mapRect } from './layoutMode';
import { useLayoutMode } from './useLayoutMode';
import { useStageZoom } from './useStageZoom';

/**
 * Casca do app (§4; FE §5.2–§5.11). Escolhe e posiciona as regiões por modo de layout; o conteúdo de cada região
 * chega por `slots` (o App liga os componentes das seções donas) e preenche a caixa da região: a geometria é deste
 * arquivo e de AppShell.css (§4.3–§4.8). A ordem do DOM é a de tabulação (§15.2.4); o CSS só posiciona.
 * `.top-bars` (SimulationBanner, PartnerSlot bar) é irmã anterior do elemento com zoom (DE-05).
 */

/** Nomes das regiões que recebem conteúdo. */
export const SHELL_SLOTS = [
  'topBars',
  'header',
  'tabs',
  'notice',
  'left',
  'card',
  'mapTop',
  'map',
  'zoom',
  'toast',
  'partnerCard',
  'stamp',
  'timeline',
  'right',
  'more',
  'footer',
  'tvTicker',
  'tvProgress',
  'tvOverlay',
  'overlays',
  // tela que toma o lugar do mapa (ORD-F-026, a de comentários): com ela, o mapa fica montado e escondido
  'screen',
] as const;
export type ShellSlot = (typeof SHELL_SLOTS)[number];
export type ShellSlots = Partial<Record<ShellSlot, ComponentChildren>>;

/** Assinatura de §12.8 que o AppShell usa (a ErrorBoundary de features/errors é compatível). */
export interface ShellBoundaryProps {
  name: string;
  quiet?: boolean;
  retryMs?: number;
  children?: ComponentChildren;
}

interface BoundarySpec {
  name: string;
  quiet?: boolean;
  retryMs?: number;
}

/** Boundaries nomeadas de §12.8 por região (a da coluna direita depende do foco, ver `rightBoundary`). */
export const SHELL_BOUNDARIES: Readonly<Partial<Record<ShellSlot, BoundarySpec>>> = Object.freeze({
  header: { name: 'header' },
  left: { name: 'left-column' },
  card: { name: 'place-card' },
  mapTop: { name: 'map-top', quiet: true },
  map: { name: 'map', quiet: true, retryMs: 2000 },
  timeline: { name: 'timeline', quiet: true },
  notice: { name: 'notice', quiet: true },
  toast: { name: 'toast', quiet: true },
  partnerCard: { name: 'partner-card', quiet: true },
  more: { name: 'more' },
  tvTicker: { name: 'tv-ticker', quiet: true },
  tvOverlay: { name: 'tv-overlay', quiet: true },
  screen: { name: 'screen' },
});

/** Coluna direita: com foco (ou na TV, só com foco de cena) é o cartão do foco; sem foco, RegionPanel + UpdatesFeed. */
export function rightBoundary(mode: LayoutMode, hasFocus: boolean): BoundarySpec {
  return { name: mode === 'tv' || hasFocus ? 'place-card' : 'right-column' };
}

/** §4.4.1: `showLeft = tv || wide || !hasFocus`; `showRight = tv ? sceneHasFocus : (wide || hasFocus)`; celular: nenhuma. */
export function shellColumns(
  mode: LayoutMode,
  stageW: number,
  hasFocus: boolean,
  tvSceneHasFocus: boolean,
): { left: boolean; right: boolean } {
  if (isPhoneMode(mode)) return { left: false, right: false };
  if (mode === 'tv') return { left: true, right: tvSceneHasFocus };
  const wide = isWide({ mode, stageW });
  return { left: wide || !hasFocus, right: wide || hasFocus };
}

/** §4.6.4 / §9.14: altura do mapa no flow, `clamp(round((W − 32) × 0,94), 240, 560)`. */
export function flowMapHeight(stageW: number): number {
  return Math.min(560, Math.max(240, Math.round((stageW - 2 * LAYOUT.PHONE_MARGIN) * 0.94)));
}

/** §4.3.3 / §12.5: o PartnerSlot `card` só aparece no desktop com retângulo do mapa ≥ 600×460. */
const PARTNER_MIN_W = 600;
const PARTNER_MIN_H = 460;

export interface AppShellProps {
  slots?: ShellSlots;
  /** ErrorBoundary de §12.8; sem ela, o conteúdo entra direto. */
  boundary?: ComponentType<ShellBoundaryProps>;
  /** TV: a cena atual tem foco (§12.2); a coluna direita da TV só aparece com ela. */
  tvSceneHasFocus?: boolean;
}

/** Mede as faixas fixas do flow e grava `--tabs-bar-h` e `--flow-sticky-h` no `<html>` (§4.6.3, §15.2.6). */
function useFlowStickyVars(
  enabled: boolean,
  topBars: { current: HTMLElement | null },
  tabs: { current: HTMLElement | null },
  notice: { current: HTMLElement | null },
): void {
  useLayoutEffect(() => {
    const rootStyle = document.documentElement.style;
    if (!enabled) {
      rootStyle.removeProperty('--tabs-bar-h');
      rootStyle.removeProperty('--flow-sticky-h');
      return;
    }
    const h = (el: HTMLElement | null) => (el ? el.getBoundingClientRect().height : 0);
    const write = () => {
      const tabsBar = h(topBars.current) + h(tabs.current);
      rootStyle.setProperty('--tabs-bar-h', `${tabsBar}px`);
      rootStyle.setProperty('--flow-sticky-h', `${tabsBar + h(notice.current)}px`);
    };
    // a 1ª escrita vem do 1º aviso do ResizeObserver, já com o layout feito: medir aqui forçava o layout da página
    // inteira na tarefa da 1ª montagem (DECISOES §49). Na carga a página está no topo e nada está grudado: o quadro
    // de espera não move nada. Sem ResizeObserver, mede já.
    if (typeof ResizeObserver !== 'function') {
      write();
      return;
    }
    const ro = new ResizeObserver(write);
    for (const el of [topBars.current, tabs.current, notice.current]) if (el) ro.observe(el);
    return () => ro.disconnect();
  }, [enabled, topBars, tabs, notice]);
}

export function AppShell({ slots = {}, boundary: Boundary, tvSceneHasFocus = false }: AppShellProps) {
  const topBarsRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  const route = useRouteState();
  const config = useConfig();

  // CASCA: Fase 11 — entrada da TV por ?tv=1 e saída quando a janela vira celular passam ao TvMode (DE-B08, §12.1).
  // Antes do useLayoutMode: o primeiro cálculo já vê `ui.tv.on` (sem quadro intermediário em desktop). O modo vem da
  // própria janela, para `?tv=1` nunca ligar a TV no celular (AC-4.1-04) mesmo sem o `layout` inicial do main.tsx.
  useLayoutEffect(() => {
    if (!route.tvOnEntry || !config.features.tv) return;
    const { result } = layoutForViewport(window.innerWidth, window.innerHeight, {
      simulation: config.mode === 'simulation',
      tv: true,
    });
    if (result.mode === 'tv') enterTv({ fullscreen: false });
  }, []);

  const { topBarsH } = useLayoutMode(topBarsRef);
  const screenSlot = slots.screen !== undefined && slots.screen !== null && slots.screen !== false;
  // com a tela escolhida, o tablet e o celular deitado rolam a página como o flow: o palco não é fixado (ORD-F-052)
  useStageZoom(shellRef, topBarsH, screenSlot);
  const { mode, stageW, stageH } = useLayout();
  const ui = useUi();
  const hasFocus = useHasFocus();
  const viewKey = useViewKey();
  const tv = mode === 'tv';
  const phone = isPhoneMode(mode);
  const flow = mode === 'flow';

  useEffect(() => {
    if (ui.tv.on && phone) exitTv();
  }, [ui.tv.on, phone]);

  // §4.6.2 (A27): no flow, mudar de foco (lugar ou perfil; a zona não conta) volta ao topo da página.
  const focusKey = `${viewKey.slice(viewKey.indexOf(':') + 1)}|${ui.profile ? `${ui.profile.office}:${ui.profile.number}` : ''}`;
  const lastFocus = useRef(focusKey);
  useLayoutEffect(() => {
    if (lastFocus.current === focusKey) return;
    lastFocus.current = focusKey;
    if (flow) window.scrollTo({ top: 0, behavior: reducedMotion.value ? 'auto' : 'smooth' });
  }, [focusKey, flow]);

  useFlowStickyVars(flow, topBarsRef, tabsRef, noticeRef);

  const cols = shellColumns(mode, stageW, hasFocus, tvSceneHasFocus);
  // ORD-F-026: com a tela de comentários, ela toma o lugar do mapa (e, no computador, da coluna direita; no celular, do
  // cartão e do "Mais"); o mapa fica montado e escondido, para a volta não recarregar a malha
  const screenOn = !tv && screenSlot;
  const rect = mapRect(stageW, stageH, cols, tv);
  const partnerOn =
    config.features.partner_slots &&
    (flow || (mode === 'desktop' && rect.x1 - rect.x0 >= PARTNER_MIN_W && rect.y1 - rect.y0 >= PARTNER_MIN_H));

  /** Conteúdo do slot dentro da boundary nomeada (quando houver). */
  const fill = (slot: ShellSlot, spec: BoundarySpec | undefined = SHELL_BOUNDARIES[slot]) => {
    const content = slots[slot];
    if (content === undefined || content === null || content === false) return null;
    if (!Boundary || !spec) return content;
    return (
      <Boundary name={spec.name} quiet={spec.quiet} retryMs={spec.retryMs}>
        {content}
      </Boundary>
    );
  };

  /** Caixa de uma região: classe `app-shell__{part}`, `data-testid="shell-{part}"` e `data-boundary` de §12.8. */
  const box = (
    slot: ShellSlot,
    part: string,
    extra: JSX.HTMLAttributes<HTMLDivElement> = {},
    spec: BoundarySpec | undefined = SHELL_BOUNDARIES[slot],
  ) => (
    <div {...extra} key={part} class={`app-shell__${part}`} data-testid={`shell-${part}`} data-boundary={spec?.name}>
      {fill(slot, spec)}
    </div>
  );

  const header = box('header', 'header');
  const notice = box('notice', 'notice', { ref: noticeRef });
  const tabs = box('tabs', 'tabs', {
    ref: tabsRef,
    style: flow ? { top: `${topBarsH}px` } : undefined,
  });
  const left = cols.left ? (
    <aside
      key="left"
      class="app-shell__left"
      data-testid="shell-left"
      data-boundary={SHELL_BOUNDARIES.left?.name}
      aria-label={t('panel.left.aria')}
    >
      {fill('left')}
    </aside>
  ) : null;
  const rightSpec = rightBoundary(mode, hasFocus);
  const right =
    cols.right && !screenOn ? (
      <aside
        key="right"
        class="app-shell__right"
        data-testid="shell-right"
        data-boundary={rightSpec.name}
        aria-label={t('panel.right.aria')}
      >
        {fill('right', rightSpec)}
      </aside>
    ) : null;
  const card = box('card', 'card');
  const stamp = box('stamp', 'stamp');
  const partner = partnerOn ? box('partnerCard', 'partner-card') : null;
  // o selo ("Fonte: TSE") vai ao pé da tela: no computador ele mora na seção do mapa, escondida com ela; no celular
  // deitado, ao lado do player, onde tiraria a altura da tela
  const screen = screenOn ? (
    <section key="screen-section" class="app-shell__screen-section" data-testid="shell-screen-section">
      {box('screen', 'screen')}
      {mode === 'desktop' || mode === 'land' ? stamp : null}
    </section>
  ) : null;
  const timelineSection = (
    <section
      key="timeline-section"
      class="app-shell__timeline-section"
      data-testid="shell-timeline-section"
      aria-label={t('shell.timeline.aria')}
    >
      {box('timeline', 'timeline')}
    </section>
  );

  /** Seção "Mapa": barra do mapa (desktop, TV e tablet), mapa, zoom e, no desktop, toast, parceiro e selo. */
  const mapSection = (
    <section
      key="map-section"
      class="app-shell__map-section"
      data-testid="shell-map-section"
      aria-label={t('shell.map.aria')}
      hidden={screenOn || undefined}
    >
      {mode === 'desktop' || tv || mode === 'tablet' ? box('mapTop', 'map-top') : null}
      {box('map', 'map', flow ? { style: { height: `${flowMapHeight(stageW)}px` } } : {})}
      {tv ? null : box('zoom', 'zoom')}
      {mode === 'desktop' ? box('toast', 'toast') : null}
      {mode === 'desktop' ? partner : null}
      {mode === 'desktop' && !screenOn ? stamp : null}
    </section>
  );

  let body: ComponentChildren;
  if (mode === 'desktop' || tv) {
    body = [
      header,
      notice,
      <main key="main" id="conteudo" class="app-shell__main" data-testid="shell-main" inert={tv || undefined}>
        {left}
        {screen}
        {mapSection}
        {tv ? null : timelineSection}
        {right}
      </main>,
      tv ? box('tvTicker', 'tv-ticker') : null,
      tv ? box('tvProgress', 'tv-progress') : null,
      tv ? box('tvOverlay', 'tv-overlay') : null,
    ];
  } else if (mode === 'land') {
    body = [
      header,
      notice,
      <main key="main" id="conteudo" class="app-shell__main" data-testid="shell-main">
        <div class="app-shell__side" data-testid="shell-side">
          {tabs}
          {/* com a tela escolhida, ela ocupa a área principal e o cartão sai (ORD-F-052; como no flow) */}
          {screenOn ? null : card}
        </div>
        {screen}
        {mapSection}
        <div class="app-shell__timeline-area" data-testid="shell-timeline-area">
          {timelineSection}
          {screenOn ? null : stamp}
        </div>
      </main>,
    ];
  } else if (mode === 'tablet') {
    body = [
      header,
      tabs,
      notice,
      <main key="main" id="conteudo" class="app-shell__main" data-testid="shell-main">
        {screenOn ? null : card}
        {screen}
        {mapSection}
        {timelineSection}
        {stamp}
      </main>,
    ];
  } else {
    // flow (§4.6.2): sem foco, cartão → mapa → linha do tempo → selo → parceiro → "Mais"; com foco, o mapa vem antes.
    const more =
      hasFocus || screenOn ? null : (
        <section
          key="more"
          class="app-shell__more"
          data-testid="shell-more"
          data-boundary={SHELL_BOUNDARIES.more?.name}
          aria-label={t('panel.more.aria')}
        >
          {fill('more')}
        </section>
      );
    const footerContent = fill('footer');
    const footer =
      footerContent === null ? (
        <div key="footer" class="app-shell__footer" data-testid="shell-footer" />
      ) : (
        <footer key="footer" class="app-shell__footer" data-testid="shell-footer">
          {footerContent}
        </footer>
      );
    body = [
      header,
      tabs,
      notice,
      <main key="main" id="conteudo" class="app-shell__main" data-testid="shell-main">
        {screenOn
          ? [screen, mapSection, timelineSection, stamp]
          : hasFocus
            ? [mapSection, timelineSection, stamp, card]
            : [card, mapSection, timelineSection, stamp]}
        {partner}
        {more}
      </main>,
      footer,
    ];
  }

  return (
    <>
      <div ref={topBarsRef} class="top-bars" data-testid="top-bars" data-mode={mode}>
        {fill('topBars')}
      </div>
      <div
        ref={shellRef}
        class="app-shell"
        data-testid="app-shell"
        data-mode={mode}
        data-left={cols.left ? '' : undefined}
        data-right={cols.right && !screenOn ? '' : undefined}
        data-screen={screenOn ? '' : undefined}
      >
        {body}
        {fill('overlays')}
      </div>
    </>
  );
}
